import { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { DollarSign, ShoppingBag, CreditCard, Users, FileDown, FileText } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const fmtDate = (d) => d.toISOString().slice(0, 10);
const COLORS = ["#00f0ff", "#39FF14", "#FFD60A", "#FF3B30", "#a78bfa"];
const methodLabel = { cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia" };

const Card = ({ label, value, icon: Icon, accent = "#00f0ff", testid }) => (
  <div data-testid={testid} className="bg-[#0a0a0a] border border-white/10 p-6">
    <div className="flex items-start justify-between"><p className="overline">{label}</p><Icon className="w-5 h-5" style={{ color: accent }} /></div>
    <p className="font-display text-5xl mt-3" style={{ color: accent }}>{value}</p>
  </div>
);

export default function Reports() {
  const today = new Date();
  const monthAgo = new Date(); monthAgo.setDate(today.getDate() - 30);
  const [from, setFrom] = useState(fmtDate(monthAgo));
  const [to, setTo] = useState(fmtDate(today));
  const [granularity, setGranularity] = useState("day");
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);

  const load = useCallback(() => {
    const qs = `from=${from}&to=${to}`;
    api.get(`/reports/summary?${qs}`).then((r) => setSummary(r.data));
    api.get(`/reports/timeseries?${qs}&granularity=${granularity}`).then((r) => setSeries(r.data));
    api.get(`/reports/top-products?${qs}`).then((r) => setTop(r.data));
  }, [from, to, granularity]);
  useEffect(() => { load(); }, [load]);

  const preset = (days) => {
    const t = new Date(); const f = new Date(); f.setDate(t.getDate() - days);
    setFrom(fmtDate(f)); setTo(fmtDate(t));
  };

  const exportUrl = (fmt) => `${API}/reports/export.${fmt}?from=${from}&to=${to}`;
  const download = async (fmt) => {
    try {
      const res = await api.get(`/reports/export.${fmt}?from=${from}&to=${to}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = fmt === "pdf" ? "reporte_glg.pdf" : "reporte_ventas.csv";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { window.open(exportUrl(fmt), "_blank"); }
  };

  const byType = summary ? [
    { name: "Membresías", value: summary.by_type.plan || 0 },
    { name: "Productos", value: summary.by_type.product || 0 },
  ] : [];
  const byMethod = summary ? Object.entries(summary.by_method).map(([k, v]) => ({ name: methodLabel[k] || k, value: v })) : [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4"><BrandLogo size="h-11" className="hidden sm:flex" /><div><p className="overline text-[#00f0ff]">Analítica</p><h1 className="font-display text-5xl">REPORTES</h1></div></div>
        <div className="flex gap-2">
          <button data-testid="export-csv-btn" onClick={() => download("csv")} className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white/5"><FileDown className="w-4 h-4" /> CSV</button>
          <button data-testid="export-pdf-btn" onClick={() => download("pdf")} className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white/5"><FileText className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 p-4 mb-6 flex flex-wrap items-end gap-4">
        <div><label className="overline block mb-1.5">Desde</label>
          <input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
        <div><label className="overline block mb-1.5">Hasta</label>
          <input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" /></div>
        <div className="flex gap-2">
          {[["Hoy", 0], ["7 días", 7], ["30 días", 30], ["90 días", 90]].map(([l, d]) => (
            <button key={l} data-testid={`preset-${d}`} onClick={() => preset(d)} className="px-3 py-2 text-xs border border-white/10 text-white/60 hover:text-white hover:border-white/30">{l}</button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          {[["day", "Día"], ["week", "Semana"], ["month", "Mes"]].map(([v, l]) => (
            <button key={v} data-testid={`gran-${v}`} onClick={() => setGranularity(v)} className={`px-3 py-2 text-xs border ${granularity === v ? "border-[#00f0ff] text-[#00f0ff]" : "border-white/10 text-white/50"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card testid="rep-revenue" label="Ingresos" value={summary ? `$${summary.revenue.toFixed(0)}` : "—"} icon={DollarSign} accent="#39FF14" />
        <Card testid="rep-sales-count" label="N.º de ventas" value={summary?.sales_count ?? "—"} icon={ShoppingBag} />
        <Card testid="rep-plan-rev" label="Ing. membresías" value={summary ? `$${(summary.by_type.plan || 0).toFixed(0)}` : "—"} icon={CreditCard} accent="#00f0ff" />
        <Card testid="rep-new-members" label="Socios nuevos" value={summary?.members?.new ?? "—"} icon={Users} accent="#FFD60A" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/10 p-6">
          <p className="overline mb-6">Ingresos por periodo</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
              <XAxis dataKey="period" stroke="#555" fontSize={11} tickLine={false} />
              <YAxis stroke="#555" fontSize={11} tickLine={false} />
              <Tooltip contentStyle={{ background: "#000", border: "1px solid #333", borderRadius: 0, color: "#fff" }} cursor={{ fill: "#00f0ff11" }} formatter={(v) => `$${v.toFixed(2)}`} />
              <Bar dataKey="revenue" fill="#00f0ff" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 p-6">
          <p className="overline mb-4">Ventas por tipo</p>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={byType} dataKey="value" nameKey="name" innerRadius={35} outerRadius={60}>
                {byType.map((e, i) => <Cell key={e.name} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#000", border: "1px solid #333", borderRadius: 0 }} formatter={(v) => `$${v.toFixed(2)}`} />
            </PieChart>
          </ResponsiveContainer>
          <p className="overline mb-2 mt-4">Por método de pago</p>
          {byMethod.length === 0 && <p className="text-white/30 text-xs">Sin datos</p>}
          {byMethod.map((m) => (
            <div key={m.name} className="flex justify-between text-sm py-1 border-b border-white/5">
              <span className="text-white/60">{m.name}</span>
              <span className="font-mono-logs text-[#39FF14]">${m.value.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#0a0a0a] border border-white/10 p-6">
          <p className="overline mb-4">Productos más vendidos</p>
          <table className="w-full text-sm" data-testid="top-products-table">
            <thead><tr className="text-left overline border-b border-white/10"><th className="py-2">Producto</th><th className="py-2 text-right">Uds.</th><th className="py-2 text-right">Ingresos</th></tr></thead>
            <tbody>
              {top.length === 0 && <tr><td colSpan="3" className="py-6 text-center text-white/30">Sin ventas de productos.</td></tr>}
              {top.map((p) => (
                <tr key={p.name} className="border-b border-white/5"><td className="py-2">{p.name}</td><td className="py-2 text-right font-mono-logs">{p.qty}</td><td className="py-2 text-right text-[#39FF14] font-mono-logs">${p.revenue.toFixed(2)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 p-6">
          <p className="overline mb-4">Socios</p>
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-white/50 text-sm">Activos</p><p className="font-display text-4xl text-[#39FF14]">{summary?.members?.active ?? "—"}</p></div>
            <div><p className="text-white/50 text-sm">Vencidos</p><p className="font-display text-4xl text-[#FF3B30]">{summary?.members?.expired ?? "—"}</p></div>
            <div><p className="text-white/50 text-sm">Nuevos (periodo)</p><p className="font-display text-4xl text-[#FFD60A]">{summary?.members?.new ?? "—"}</p></div>
            <div><p className="text-white/50 text-sm">Total</p><p className="font-display text-4xl">{summary?.members?.total ?? "—"}</p></div>
          </div>
        </div>
      </div>
    </div>
  );
}
